import type { AxiosInstance } from "axios";

export function propertyPhotosDownloadZipPath(propertyId: number): string {
  return `/api/adm/properties/${propertyId}/photos/download-zip`;
}

export type DownloadPropertyPhotosZipAuth = {
  api: AxiosInstance;
  getAccessToken: () => Promise<string | undefined | null>;
};

export type DownloadPropertyPhotosZipOptions = {
  onProgress?: (loaded: number, total?: number) => void;
};

async function blobLooksLikeZipFile(blob: Blob): Promise<boolean> {
  if (blob.size < 2) return false;
  const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
  return head[0] === 0x50 && head[1] === 0x4b;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  const run = (): void => {
    try {
      a.click();
    } finally {
      a.remove();
    }
    window.setTimeout(() => URL.revokeObjectURL(href), 120_000);
  };
  window.setTimeout(run, 0);
}

function parseFilenameFromContentDisposition(cd: string | undefined): string | null {
  if (!cd || typeof cd !== "string") return null;
  const utf8 = cd.match(/filename\*=UTF-8''([^;\s]+)/i);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].replace(/"/g, "").trim());
    } catch {
      return null;
    }
  }
  const simple = cd.match(/filename=["']?([^"';]+)["']?/i);
  return simple?.[1]?.trim() ?? null;
}

function contentDispositionFromHeaders(res: {
  headers?: Record<string, string | undefined> & {
    get?: (name: string) => string | undefined;
  };
}): string | undefined {
  const h = res.headers as
    | {
        "content-disposition"?: string;
        get?: (name: string) => string | undefined;
      }
    | undefined;
  if (!h) return undefined;
  return (
    h["content-disposition"] ??
    (typeof h.get === "function" ? h.get("content-disposition") : undefined)
  );
}

async function messageFromErrorBlob(blob: Blob | undefined): Promise<string | undefined> {
  if (!(blob instanceof Blob)) return undefined;
  try {
    const text = await blob.text();
    const j = JSON.parse(text) as { message?: string };
    return typeof j?.message === "string" ? j.message : undefined;
  } catch {
    return undefined;
  }
}

async function interpretZipBlob(blob: Blob): Promise<{ blob: Blob } | { error: string }> {
  if (!(blob instanceof Blob) || blob.size === 0) {
    return { error: "Resposta vazia ao baixar o ZIP." };
  }

  if (await blobLooksLikeZipFile(blob)) {
    return { blob };
  }

  const text = await blob.text();
  const trimmed = text.trimStart();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const j = JSON.parse(text) as { message?: string };
      return { error: j?.message || "O servidor retornou erro em vez do arquivo ZIP." };
    } catch {
      return { error: "Resposta inválida (não é um arquivo ZIP)." };
    }
  }

  if (blob.type.includes("json") || blob.type.includes("text")) {
    return { error: "O servidor retornou texto/JSON em vez do arquivo ZIP." };
  }

  return { error: "Resposta não é um arquivo ZIP (assinatura inválida)." };
}

export async function downloadPropertyPhotosZip(
  propertyId: number,
  auth: DownloadPropertyPhotosZipAuth,
  options?: DownloadPropertyPhotosZipOptions
): Promise<void> {
  const token = await auth.getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const defaultFilename = `imovel-${propertyId}-fotos.zip`;
  const path = propertyPhotosDownloadZipPath(propertyId);

  try {
    const res = await auth.api.get<Blob>(path, {
      responseType: "blob",
      headers,
      timeout: 900_000,
      transformResponse: [(data) => data],
      onDownloadProgress: (e) => {
        const total = e.total && e.total > 0 ? e.total : undefined;
        options?.onProgress?.(e.loaded, total);
      },
    });

    const rawBlob = res.data;
    const contentDisposition = contentDispositionFromHeaders(res);

    const interpreted = await interpretZipBlob(rawBlob);
    if ("error" in interpreted) {
      throw new Error(interpreted.error);
    }

    const filename =
      parseFilenameFromContentDisposition(contentDisposition) || defaultFilename;

    triggerBlobDownload(interpreted.blob, filename);
  } catch (err: unknown) {
    const ax = err as {
      code?: string;
      message?: string;
      response?: { status?: number; data?: Blob };
    };

    if (ax.code === "ECONNABORTED" || /timeout/i.test(ax.message ?? "")) {
      throw new Error(
        "Tempo limite esgotado ao baixar o ZIP. Imóveis com muitas fotos podem levar vários minutos no servidor — tente de novo ou peça otimização da rota no back-end."
      );
    }

    const status = ax?.response?.status;

    if (status === 404) {
      throw new Error(
        "Download ZIP não encontrado (404). Confirme no back-end: " +
          "`GET /api/adm/properties/{id}/photos/download-zip` ativo neste ambiente."
      );
    }
    if (status === 401 || status === 403) {
      throw new Error("Sessão expirada ou sem permissão para baixar as fotos.");
    }

    const apiMessage = await messageFromErrorBlob(ax?.response?.data);
    if (apiMessage) {
      throw new Error(apiMessage);
    }

    throw new Error(
      "Não foi possível baixar o ZIP das fotos. Tente novamente ou verifique o servidor."
    );
  }
}
